
<div class="min-h-screen bg-slate-100">
  <header class="bg-white shadow-sm">
    <div class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex items-center space-x-4">
        <a routerLink="/applications" class="text-slate-500 hover:text-slate-800">
          <!-- Heroicon: arrow-left -->
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
        </a>
        <h1 class="text-2xl font-bold text-slate-900">Application Details</h1>
      </div>
    </div>
  </header>

  <main class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    @if (loading()) {
      <p class="text-center text-slate-500">Loading application details...</p>
    } @else if (error()) {
      <div class="bg-red-50 border-l-4 border-red-400 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">{{ error() }}</p>
          </div>
        </div>
      </div>
    } @else if (application(); as app) {
      @let appData = mergedApplicationData();
      <div class="lg:grid lg:grid-cols-3 lg:gap-8">
        <!-- Left column: Application Data & Documents -->
        <div class="lg:col-span-2 space-y-8">
          <!-- Application Info Header -->
          <div class="bg-white p-6 rounded-lg shadow">
            <div class="flex justify-between items-start">
              <div>
                <h2 class="text-lg font-semibold text-slate-800">Applicant: {{ appData?.['fleet_name'] || 'N/A' }}</h2>
                <p class="text-sm text-slate-500 mt-1">Application ID: {{ app.id }}</p>
              </div>
              <span [class]="'px-3 py-1 text-sm font-semibold rounded-full ' + getStatusClass(app.status)">
                {{ app.status.replace('_', ' ') | titlecase }}
              </span>
            </div>
          </div>
          
          @if(appData) {
            <!-- Grid for contact details and map -->
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <!-- Fleet Contact Details -->
              <div class="bg-white rounded-lg shadow">
                <header class="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between">
                  <h3 class="text-lg font-semibold text-slate-900">Fleet Contact Details</h3>
                  @if(canEditContact() && !isEditingContact()) {
                    <button (click)="startEditContact()" class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-md px-2.5 py-1.5 hover:bg-slate-100 transition-colors">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                      Edit
                    </button>
                  }
                  @if(isEditingContact()) {
                    <div class="flex items-center gap-2">
                      <button (click)="cancelEditContact()" [disabled]="isSavingContact()" class="text-sm font-medium text-slate-600 hover:text-slate-900 rounded-md px-3 py-1.5 hover:bg-slate-100 transition-colors disabled:opacity-50">
                        Cancel
                      </button>
                      <button (click)="saveContactDetails()" [disabled]="isSavingContact()" class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50">
                        @if(isSavingContact()) {
                          <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        }
                        Save
                      </button>
                    </div>
                  }
                </header>

                @if(isEditingContact()) {
                  <!-- Edit form -->
                  <div class="p-4 sm:p-6 space-y-4">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div class="sm:col-span-2">
                        <label class="block text-sm font-medium text-slate-700 mb-1">Fleet Name</label>
                        <input type="text" [value]="editContactForm()!.fleet_name" (input)="updateEditField('fleet_name', $any($event.target).value)" class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div class="sm:col-span-2">
                        <label class="block text-sm font-medium text-slate-700 mb-1">Address</label>
                        <input #editAddressInput type="text" [value]="editAddressInputText()" (input)="editAddressInputText.set($any($event.target).value)" placeholder="Search for an address..." class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" autocomplete="off" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                        <input type="text" [value]="editContactForm()!.contact_name" (input)="updateEditField('contact_name', $any($event.target).value)" class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input type="email" [value]="editContactForm()!.contact_email" (input)="updateEditField('contact_email', $any($event.target).value)" class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                        <input type="tel" [value]="editContactForm()!.contact_phone" (input)="updateEditField('contact_phone', $any($event.target).value)" class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">Fleet Phone</label>
                        <input type="tel" [value]="editContactForm()!.fleet_phone" (input)="updateEditField('fleet_phone', $any($event.target).value)" class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    @if(saveContactError()) {
                      <p class="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{{ saveContactError() }}</p>
                    }
                  </div>
                } @else {
                  <!-- Read-only view -->
                  <div class="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                    <div class="flex items-start col-span-2">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6m-6 3h6m-6 3h6m-6 3h6m-6 3h6" /></svg>
                      <div class="ml-3">
                        <dt class="text-sm font-medium text-slate-500">Fleet Name</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ appData['fleet_name'] || 'N/A' }}</dd>
                      </div>
                    </div>
                    <div class="flex items-start col-span-2">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                      <div class="ml-3">
                        <dt class="text-sm font-medium text-slate-500">Address</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ formattedAddress() }}</dd>
                      </div>
                    </div>
                    <div class="flex items-start">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                      <div class="ml-3">
                        <dt class="text-sm font-medium text-slate-500">Contact Name</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ appData['contact_name'] || 'N/A' }}</dd>
                      </div>
                    </div>
                    <div class="flex items-start col-span-2">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                      <div class="ml-3 min-w-0">
                        <dt class="text-sm font-medium text-slate-500">Email</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5 break-all">{{ appData['contact_email'] || 'N/A' }}</dd>
                      </div>
                    </div>
                    <div class="flex items-start">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                      <div class="ml-3">
                        <dt class="text-sm font-medium text-slate-500">Contact Phone</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ appData['contact_phone'] || 'N/A' }}</dd>
                      </div>
                    </div>
                    <div class="flex items-start">
                      <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                      <div class="ml-3">
                        <dt class="text-sm font-medium text-slate-500">Fleet Phone</dt>
                        <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ appData['fleet_phone'] || 'N/A' }}</dd>
                      </div>
                    </div>
                  </div>
                }
              </div>

              <!-- Fleet Location Map -->
              @if(appData['latitude'] && appData['longitude']) {
                <div class="bg-white rounded-lg shadow">
                  <header class="p-4 sm:p-6 border-b border-slate-200">
                    <h3 class="text-lg font-semibold text-slate-900">Fleet Location</h3>
                  </header>
                  <div class="p-4 sm:p-6">
                    <div #map id="map" class="h-80 w-full rounded-md"></div>
                  </div>
                </div>
              }
            </div>

            <!-- Customer Validation -->
            @if(validationRequired()) {
              <div class="bg-white rounded-lg shadow">
                <header class="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 class="text-lg font-semibold text-slate-900">Customer Validation</h3>
                    <p class="text-sm text-slate-500 mt-0.5">Validate the fleet name and phone number against existing records before starting review.</p>
                  </div>
                  @if(validationStatus() === 'idle' || validationStatus() === 'invalid') {
                    <button (click)="validateCustomer()" class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500">
                      @if(validationStatus() === 'invalid') { Re-validate } @else { Validate Customer }
                    </button>
                  }
                  @if(validationStatus() === 'loading') {
                    <div class="inline-flex items-center gap-2 text-sm text-slate-500">
                      <svg class="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      Validating...
                    </div>
                  }
                </header>
                <div class="p-4 sm:p-6">
                  @switch(validationStatus()) {
                    @case('idle') {
                      <p class="text-sm text-slate-600">Click <strong>Validate Customer</strong> to check the fleet name and phone against existing records.</p>
                    }
                    @case('loading') {
                      <p class="text-sm text-slate-500">Checking fleet details against existing customer records...</p>
                    }
                    @case('valid') {
                      <div class="flex items-center gap-3">
                        <div class="flex-shrink-0 rounded-full bg-green-100 p-1.5">
                          <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        </div>
                        <p class="text-sm font-medium text-green-800">Customer validated — this application can be approved.</p>
                        <button (click)="validateCustomer()" class="ml-auto text-xs text-slate-400 hover:text-slate-600">Re-validate</button>
                      </div>
                    }
                    @case('invalid') {
                      <div class="flex items-start gap-3">
                        <div class="flex-shrink-0 rounded-full bg-red-100 p-1.5 mt-0.5">
                          <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-red-800">Validation failed — fleet details match existing records.</p>
                          <p class="text-sm text-red-700 mt-1">Please update the fleet phone using the <strong>Edit</strong> button in Fleet Contact Details above, then re-validate before approving.</p>
                        </div>
                      </div>
                    }
                  }
                  @if(validationError()) {
                    <p class="mt-3 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{{ validationError() }}</p>
                  }
                </div>
              </div>
            }

            <!-- Fleet Operations -->
            <div class="bg-white rounded-lg shadow">
              <header class="p-4 sm:p-6 border-b border-slate-200">
                <h3 class="text-lg font-semibold text-slate-900">Fleet Operations</h3>
              </header>
              <div class="p-4 sm:p-6 space-y-6">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <dt class="text-sm font-medium text-slate-500">Fleet Size</dt>
                    <dd class="text-base font-semibold text-slate-900 mt-1">{{ appData['fleet_size_band'] || 'N/A' }}</dd>
                  </div>
                   <div>
                    <dt class="text-sm font-medium text-slate-500">Service Level</dt>
                    <dd class="text-base font-semibold text-slate-900 mt-1">{{ appData['service_level'] || 'N/A' }}</dd>
                  </div>
                   <div>
                    <dt class="text-sm font-medium text-slate-500">Dispatch System</dt>
                    <dd class="text-base font-semibold text-slate-900 mt-1">{{ appData['dispatch_system'] || 'N/A' }}</dd>
                  </div>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-slate-500">Booking Mix</h4>
                  <div class="mt-2 w-full bg-slate-100 rounded-full h-2.5">
                    <div class="bg-blue-600 h-2.5 rounded-full" [style.width.%]="appData['prebook_pct'] || 0"></div>
                  </div>
                  <div class="flex justify-between mt-2 text-sm">
                    <span class="font-medium text-blue-700">Pre-book: {{ appData['prebook_pct'] || 0 }}%</span>
                    <span class="font-medium text-slate-600">ASAP: {{ appData['asap_pct'] || 0 }}%</span>
                  </div>
                </div>
                 <div>
                  <h4 class="text-sm font-medium text-slate-500">Payment Split</h4>
                  <div class="mt-2 flex w-full h-2.5 rounded-full overflow-hidden bg-slate-100">
                      <div class="bg-purple-600" [style.width.%]="appData['account_pct'] || 0"></div>
                      <div class="bg-teal-500" [style.width.%]="appData['card_pct'] || 0"></div>
                      <div class="bg-slate-400" [style.width.%]="appData['cash_pct'] || 0"></div>
                  </div>
                  <div class="flex justify-between mt-2 text-sm">
                    <span class="font-medium text-purple-700">Account: {{ appData['account_pct'] || 0 }}%</span>
                    <span class="font-medium text-teal-600">Card: {{ appData['card_pct'] || 0 }}%</span>
                    <span class="font-medium text-slate-600">Cash: {{ appData['cash_pct'] || 0 }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Agreements -->
            <div class="bg-white rounded-lg shadow">
              <header class="p-4 sm:p-6 border-b border-slate-200">
                <h3 class="text-lg font-semibold text-slate-900">Agreements</h3>
              </header>
              <ul class="p-4 sm:p-6 divide-y divide-slate-100">
                <li class="py-3 flex items-center justify-between">
                  <span class="font-medium text-slate-800">Terms & Conditions</span>
                  @if(appData['terms_accepted']) {
                    <div class="flex items-center text-green-600">
                       <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                       @if (appData['terms_accepted_at']) {
                        <span class="text-sm">Agreed on {{ formatDate(appData['terms_accepted_at']) }}</span>
                       } @else {
                        <span class="text-sm">Agreed</span>
                       }
                    </div>
                  } @else { <span class="text-sm text-slate-500">Not Agreed</span> }
                </li>
                 <li class="py-3 flex items-center justify-between">
                  <span class="font-medium text-slate-800">Data Sharing Agreement</span>
                  @if(appData['data_sharing_accepted']) {
                    <div class="flex items-center text-green-600">
                       <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        @if (appData['data_sharing_accepted_at']) {
                          <span class="text-sm">Agreed on {{ formatDate(appData['data_sharing_accepted_at']) }}</span>
                        } @else {
                          <span class="text-sm">Agreed</span>
                        }
                    </div>
                  } @else { <span class="text-sm text-slate-500">Not Agreed</span> }
                </li>
                 <li class="py-3 flex items-center justify-between">
                  <span class="font-medium text-slate-800">Code of Conduct</span>
                   @if(appData['code_of_conduct_accepted']) {
                    <div class="flex items-center text-green-600">
                       <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        @if (appData['code_of_conduct_accepted_at']) {
                          <span class="text-sm">Agreed on {{ formatDate(appData['code_of_conduct_accepted_at']) }}</span>
                        } @else {
                          <span class="text-sm">Agreed</span>
                        }
                    </div>
                  } @else { <span class="text-sm text-slate-500">Not Agreed</span> }
                </li>
              </ul>
            </div>

            <!-- Fleet Notes -->
            <div class="bg-white rounded-lg shadow">
              <header class="p-4 sm:p-6 border-b border-slate-200">
                <h3 class="text-lg font-semibold text-slate-900">Fleet Notes</h3>
              </header>
              <div class="p-4 sm:p-6">
                @if (appData['additional_notes']) {
                  <p class="text-sm text-slate-700 whitespace-pre-wrap">{{ appData['additional_notes'] }}</p>
                } @else {
                  <p class="text-sm text-slate-500 italic">No notes provided</p>
                }
              </div>
            </div>
          } @else {
            <div class="bg-white p-6 rounded-lg shadow">
              <p class="text-slate-500 text-sm text-center">No application data submitted.</p>
            </div>
          }

          <!-- Documents -->
          <div class="bg-white p-6 rounded-lg shadow">
            <h3 class="text-base font-semibold leading-7 text-slate-900">Submitted Documents</h3>
            <div class="mt-4 border-t border-slate-200">
              <dl class="divide-y divide-slate-200">
                @for(doc of enhancedDocuments(); track doc.id) {
                  <div (click)="openDocumentViewer(doc)" class="px-2 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors">
                    <dt class="text-sm font-medium leading-6 text-slate-900 flex items-center">
                      <svg class="h-5 w-5 text-slate-400 mr-2" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>
                      {{ formatDocumentType(doc.document_type) }}
                    </dt>
                    <dd class="mt-1 text-sm leading-6 text-slate-700 sm:col-span-2 sm:mt-0 flex justify-between items-center">
                      @if (doc.status === 'approved') {
                        <div class="flex items-center">
                          <span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Approved</span>
                          @if(doc.expiry_date) {
                            <span class="ml-3 text-xs text-slate-500">Expires: {{ formatDate(doc.expiry_date) }}</span>
                          }
                        </div>
                      } @else {
                        <span class="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending Review</span>
                      }
                      <span class="text-indigo-600 font-medium text-sm">View Document &rarr;</span>
                    </dd>
                  </div>
                } @empty {
                  <div class="px-4 py-6 text-center text-sm text-slate-500">No documents submitted.</div>
                }
              </dl>
            </div>
          </div>
        </div>

        <!-- Right column: Actions & History -->
        <div class="mt-8 lg:mt-0 space-y-8">
          <!-- Actions / Decision -->
          <div class="bg-white p-6 rounded-lg shadow">
            @if (app.status === 'submitted') {
              <h3 class="text-base font-semibold text-slate-900">Actions</h3>
              <div class="mt-4 space-y-3">
                <button
                  (click)="startReview()"
                  [disabled]="actionInProgress() || !canStartReview()"
                  [title]="!canStartReview() ? 'Customer must be validated before starting review.' : 'Start Review'"
                  class="w-full inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-slate-300 disabled:cursor-not-allowed">
                  Start Review
                </button>
                @if (!canStartReview()) {
                  <p class="mt-2 text-xs text-center text-slate-500">
                    Customer must be validated before starting review.
                  </p>
                }
              </div>
            } @else if (app.status === 'in_review') {
              <h3 class="text-base font-semibold text-slate-900">Actions</h3>
              <div class="mt-4 space-y-3">
                <div>
                  <button
                    (click)="openActionModal('approved')"
                    [disabled]="actionInProgress() || !allDocumentsApproved() || !canApprove()"
                    [title]="!allDocumentsApproved() ? 'All documents must be approved first.' : !canApprove() ? 'Customer must be validated before approving.' : 'Approve Application'"
                    class="w-full inline-flex justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:bg-slate-300 disabled:cursor-not-allowed">
                    Approve
                  </button>
                  @if (!allDocumentsApproved()) {
                    <p class="mt-2 text-xs text-center text-slate-500">
                      All documents must be approved before approving the application.
                    </p>
                  }
                  @if (allDocumentsApproved() && !canApprove()) {
                    <p class="mt-2 text-xs text-center text-slate-500">
                      Customer must be validated before approving.
                    </p>
                  }
                </div>
                <button (click)="openActionModal('rejected')" [disabled]="actionInProgress()" class="w-full inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:bg-slate-300 disabled:cursor-not-allowed">Reject</button>
                <button (click)="returnToStatus('submitted')" [disabled]="actionInProgress()" class="w-full inline-flex justify-center rounded-md bg-white border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">Return to Submitted</button>
              </div>
            } @else if (app.decision) {
              <h3 class="text-base font-semibold text-slate-900">Decision</h3>
              <div class="mt-4 space-y-3">
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-slate-500">Outcome</span>
                    <span class="text-base font-bold capitalize" [class.text-green-600]="app.decision === 'approved'" [class.text-red-600]="app.decision === 'rejected'">{{ app.decision }}</span>
                  </div>
                  @if (app.decision_at) {
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-slate-500">Date</span>
                    <span class="text-base font-semibold text-slate-900">{{ formatDate(app.decision_at) }}</span>
                  </div>
                  }
                  @if (app.decision_notes) {
                  <div>
                    <p class="text-sm font-medium text-slate-500 mb-2">Notes</p>
                    <p class="text-sm text-slate-700 p-3 bg-slate-50 rounded-md border border-slate-200 whitespace-pre-wrap leading-relaxed">{{ app.decision_notes }}</p>
                  </div>
                  }
              </div>
              @if (app.decision === 'rejected') {
                <div class="mt-4 space-y-3">
                  <button (click)="returnToStatus('submitted')" [disabled]="actionInProgress()" class="w-full inline-flex justify-center rounded-md bg-white border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">Return to Submitted</button>
                  <button (click)="returnToStatus('in_review')" [disabled]="actionInProgress()" class="w-full inline-flex justify-center rounded-md bg-white border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">Return to Review</button>
                </div>
              }
              @if (app.decision === 'approved') {
                @let status = buildStatus();
                <div class="mt-4 pt-4 border-t border-slate-200">
                    <h4 class="text-sm font-medium text-slate-500">System Build Status</h4>
                    <div class="mt-2 flex items-center space-x-3">
                        <span [class]="'px-3 py-1 text-sm font-semibold rounded-full ' + status.class">
                          {{ status.text }}
                        </span>
                        @if (latestWebhook(); as webhook) {
                            @if (webhook.last_error) {
                               <p class="text-sm text-red-600" title="{{ webhook.last_error }}">
                                  Error: {{ webhook.last_error.length > 50 ? webhook.last_error.substring(0, 50) + '...' : webhook.last_error }}
                               </p>
                            }
                        }
                    </div>
                    @if (isBuildFailed()) {
                        <div class="mt-3">
                            <button
                                type="button"
                                (click)="revertApprovedApplication()"
                                [disabled]="revertingApproved() || actionInProgress()"
                                title="Remove the failed build entry and return this application to submitted status"
                                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                @if (revertingApproved()) {
                                    <svg class="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Reverting...
                                } @else {
                                    <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                                    </svg>
                                    Return to Submitted
                                }
                            </button>
                        </div>
                    }
                </div>

                <!-- System Details -->
                @if (application()?.system_details; as systemDetails) {
                  <div class="mt-4 pt-4 border-t border-slate-200">
                    <h4 class="text-sm font-medium text-slate-500 mb-3">System Details</h4>
                    <div class="space-y-4">
                      <div class="flex items-start">
                        <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm-3.375 6.166a3.375 3.375 0 016.75 0H7.125z" /></svg>
                        <div class="ml-3">
                          <dt class="text-sm font-medium text-slate-500">Manager ID</dt>
                          <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ systemDetails.manager_id }}</dd>
                        </div>
                      </div>
                      <div class="flex items-start">
                        <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>
                        <div class="ml-3">
                          <dt class="text-sm font-medium text-slate-500">Logon Domain</dt>
                          <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ systemDetails.logon_domain }}</dd>
                        </div>
                      </div>
                      <div class="flex items-start">
                        <svg class="h-6 w-6 text-slate-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" /></svg>
                        <div class="ml-3">
                          <dt class="text-sm font-medium text-slate-500">Account Short Name</dt>
                          <dd class="text-base font-semibold text-slate-900 mt-0.5">{{ systemDetails.account_short_name }}</dd>
                        </div>
                      </div>
                    </div>
                    @if (app.status === 'ready_for_go_live' || app.status === 'live') {
                      <!-- Portal Credentials Panel -->
                      <div class="mt-5 rounded-lg overflow-hidden" style="background:#eef2ff;border:1px solid #c7d2fe;border-left:3px solid #4f46e5;">
                        <!-- Header -->
                        <div class="flex items-center justify-between px-3 py-2.5" style="border-bottom:1px solid #c7d2fe;">
                          <div class="flex items-center gap-2.5">
                            <div style="width:26px;height:26px;border-radius:6px;background:#e0e7ff;border:1px solid #c7d2fe;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                              @if (showPortalCredentials()) {
                                <svg style="width:13px;height:13px;color:#4f46e5;" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                              } @else {
                                <svg style="width:13px;height:13px;color:#4f46e5;" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                              }
                            </div>
                            <div>
                              <p style="font-size:10px;font-weight:700;letter-spacing:0.09em;color:#6366f1;text-transform:uppercase;margin:0;line-height:1.2;">Portal Access</p>
                              <p style="font-size:12.5px;font-weight:600;color:#1e293b;margin:0;line-height:1.4;">SuperYes Admin</p>
                            </div>
                          </div>
                          <button
                            (click)="togglePortalCredentials()"
                            [disabled]="portalCredentialsLoading()"
                            style="display:inline-flex;align-items:center;gap:4px;padding:4px 11px;border-radius:5px;border:none;background:#4f46e5;color:#fff;font-size:11.5px;font-weight:600;cursor:pointer;transition:opacity 0.15s ease;white-space:nowrap;outline:none;letter-spacing:0.01em;"
                            [style.opacity]="portalCredentialsLoading() ? '0.6' : '1'"
                            [style.cursor]="portalCredentialsLoading() ? 'not-allowed' : 'pointer'"
                          >
                            @if (portalCredentialsLoading()) {
                              <svg class="portal-spin" style="width:11px;height:11px;" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                              <span>Loading</span>
                            } @else if (showPortalCredentials()) {
                              <svg style="width:11px;height:11px;" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>
                              <span>Hide</span>
                            } @else {
                              <svg style="width:11px;height:11px;" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                              <span>Show</span>
                            }
                          </button>
                        </div>

                        <!-- Error state -->
                        @if (portalCredentialsError()) {
                          <div style="padding:8px 12px;background:#fef2f2;border-bottom:1px solid #fecaca;">
                            <p style="font-size:12px;color:#dc2626;margin:0;">{{ portalCredentialsError() }}</p>
                          </div>
                        }

                        <!-- Revealed credentials -->
                        @if (showPortalCredentials() && portalCredentials(); as creds) {
                          <div class="portal-creds-reveal" style="padding:12px;display:flex;flex-direction:column;gap:8px;">
                            <!-- Username row -->
                            <div>
                              <p style="font-size:10px;font-weight:700;letter-spacing:0.1em;color:#818cf8;text-transform:uppercase;margin:0 0 4px 0;">Username</p>
                              <div style="display:flex;align-items:center;background:#fff;border:1px solid #e2e8f0;border-radius:6px;padding:8px 8px 8px 11px;gap:6px;box-shadow:0 1px 2px rgba(0,0,0,0.04);">
                                <span style="font-family:'Courier New',Courier,monospace;font-size:13px;color:#1e293b;font-weight:500;word-break:break-all;flex:1;min-width:0;">{{ creds.username }}</span>
                                <button
                                  (click)="copyToClipboard(creds.username, 'username')"
                                  title="Copy username"
                                  style="width:26px;height:26px;border-radius:5px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.15s ease;outline:none;"
                                  [style.background]="copiedField() === 'username' ? '#dcfce7' : '#f8fafc'"
                                  [style.border]="copiedField() === 'username' ? '1px solid #86efac' : '1px solid #e2e8f0'"
                                >
                                  @if (copiedField() === 'username') {
                                    <svg style="width:12px;height:12px;color:#16a34a;" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                  } @else {
                                    <svg style="width:12px;height:12px;color:#94a3b8;" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg>
                                  }
                                </button>
                              </div>
                            </div>
                            <!-- Password row -->
                            <div>
                              <p style="font-size:10px;font-weight:700;letter-spacing:0.1em;color:#818cf8;text-transform:uppercase;margin:0 0 4px 0;">Temp. Password</p>
                              <div style="display:flex;align-items:center;background:#fff;border:1px solid #e2e8f0;border-radius:6px;padding:8px 8px 8px 11px;gap:6px;box-shadow:0 1px 2px rgba(0,0,0,0.04);">
                                <span style="font-family:'Courier New',Courier,monospace;font-size:13px;color:#1e293b;font-weight:500;word-break:break-all;flex:1;min-width:0;">{{ creds.tempPassword }}</span>
                                <button
                                  (click)="copyToClipboard(creds.tempPassword, 'password')"
                                  title="Copy password"
                                  style="width:26px;height:26px;border-radius:5px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.15s ease;outline:none;"
                                  [style.background]="copiedField() === 'password' ? '#dcfce7' : '#f8fafc'"
                                  [style.border]="copiedField() === 'password' ? '1px solid #86efac' : '1px solid #e2e8f0'"
                                >
                                  @if (copiedField() === 'password') {
                                    <svg style="width:12px;height:12px;color:#16a34a;" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                                  } @else {
                                    <svg style="width:12px;height:12px;color:#94a3b8;" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg>
                                  }
                                </button>
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }

                <!-- Next Step Actions -->
                <div class="mt-4 pt-4 border-t border-slate-200">
                    <h4 class="text-sm font-medium text-slate-500">Next Steps</h4>
                    <div class="mt-2 space-y-2">
                      @if (app.status === 'approved') {
                        <button
                          (click)="advanceStatus('ready_for_go_live')"
                          [disabled]="actionInProgress() || !isBuildComplete()"
                          class="w-full inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                          Mark as Ready for Go Live
                        </button>
                        @if (!isBuildComplete()) {
                          <p class="text-xs text-slate-500 text-center">
                            ⏳ Waiting for instance build to complete
                          </p>
                        }
                      } @else if (app.status === 'ready_for_go_live') {
                         <button (click)="advanceStatus('live')" [disabled]="actionInProgress()" class="w-full inline-flex justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:bg-slate-300">
                          Go Live
                        </button>
                      } @else if (app.status === 'live') {
                        <p class="text-sm text-green-700 bg-green-50 p-3 rounded-md text-center font-semibold">Application is Live</p>
                      } @else if (app.status === 'suspended') {
                        <p class="text-sm text-orange-700 bg-orange-50 p-3 rounded-md text-center font-semibold">System is Suspended</p>
                      }
                      @if (['approved', 'ready_for_go_live', 'live'].includes(app.status)) {
                        <button
                          (click)="openSuspendModal()"
                          [disabled]="actionInProgress()"
                          class="w-full inline-flex justify-center rounded-md bg-white border border-orange-300 px-3 py-2 text-sm font-semibold text-orange-600 shadow-sm hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Suspend System
                        </button>
                      }
                      @if (app.status === 'suspended') {
                        <button
                          (click)="openReinstateModal()"
                          [disabled]="actionInProgress()"
                          class="w-full inline-flex justify-center rounded-md bg-lime-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-lime-500 disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                          Reinstate System
                        </button>
                      }
                      @if (['approved', 'ready_for_go_live', 'live'].includes(app.status)) {
                        <button
                          (click)="openRollbackModal()"
                          [disabled]="actionInProgress()"
                          class="w-full inline-flex justify-center rounded-md bg-white border border-red-300 px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Reject and Remove
                        </button>
                      }
                      @if (suspendResult(); as result) {
                        <p class="text-xs font-medium text-center" [class.text-green-600]="result.ok" [class.text-red-600]="!result.ok">
                          {{ result.ok ? '✓' : '⚠' }} {{ result.message }}
                        </p>
                      }
                      @if (reinstateResult(); as result) {
                        <p class="text-xs font-medium text-center" [class.text-green-600]="result.ok" [class.text-red-600]="!result.ok">
                          {{ result.ok ? '✓' : '⚠' }} {{ result.message }}
                        </p>
                      }
                    </div>
                </div>
              }
            }
          </div>

          <!-- Send Legal Documents -->
          <div class="bg-white p-6 rounded-lg shadow">
            <h3 class="text-base font-semibold text-slate-900">Legal Documents</h3>
            <div class="mt-4">
              @if (!showLegalDocsConfirm()) {
                <button
                  (click)="promptSendLegalDocuments()"
                  class="w-full inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                >
                  <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  Email Legal Documents
                </button>
              } @else {
                <div class="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <p class="text-sm text-slate-700">The following documents will be sent as PDF attachments:</p>
                  <ul class="mt-2 text-sm text-slate-600 list-disc list-inside space-y-1">
                    <li>Terms of Service</li>
                    <li>Code of Conduct</li>
                    <li>Data Sharing Agreement</li>
                  </ul>
                  <div class="mt-3 pt-3 border-t border-slate-200">
                    <p class="text-sm text-slate-500">Recipient</p>
                    <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ mergedApplicationData()?.['contact_email'] || 'No email on file' }}</p>
                  </div>
                  <div class="mt-4 flex gap-2">
                    <button
                      (click)="confirmSendLegalDocuments()"
                      [disabled]="sendingLegalDocs() || !mergedApplicationData()?.['contact_email']"
                      class="flex-1 inline-flex justify-center items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      @if (sendingLegalDocs()) {
                        <svg class="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      } @else {
                        Confirm & Send
                      }
                    </button>
                    <button
                      (click)="cancelSendLegalDocuments()"
                      [disabled]="sendingLegalDocs()"
                      class="flex-1 inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                  @if (sendLegalDocsError(); as err) {
                    <p class="mt-2 text-xs text-red-600 text-center">{{ err }}</p>
                  }
                </div>
              }
              @if (sendLegalDocsSuccess()) {
                <p class="mt-2 text-xs text-green-600 font-medium text-center">&#10003; Legal documents sent successfully.</p>
              }
            </div>
          </div>

          <!-- History -->
          <div class="bg-white p-6 rounded-lg shadow">
            <h3 class="text-base font-semibold text-slate-900">History</h3>
            <ul role="list" class="mt-4 space-y-4">
              @for (log of enhancedLogs(); track log.id) {
                <li class="relative flex gap-x-4">
                  <div class="absolute left-0 top-0 flex w-6 justify-center -bottom-4">
                    <div class="w-px bg-slate-200"></div>
                  </div>
                  <div class="relative flex h-6 w-6 flex-none items-center justify-center bg-white">
                    <div class="h-1.5 w-1.5 rounded-full bg-slate-300 ring-1 ring-slate-400"></div>
                  </div>
                  <div class="flex-auto py-0.5 text-xs leading-5 text-slate-500">
                    <p class="font-medium text-slate-900">{{ log.action }} by {{ log.actor_name }}</p>
                    @if(log.description) {
                      <p class="text-slate-600 italic">"{{ log.description }}"</p>
                    }
                    <time [attr.datetime]="log.created_at" class="text-slate-400">{{ formatDate(log.created_at) }}</time>
                  </div>
                </li>
              } @empty {
                <p class="text-sm text-slate-500">No history for this application.</p>
              }
            </ul>
          </div>
        </div>
      </div>
    }
  </main>

  <!-- Rejection Modal -->
  @if (showRejectionModal()) {
    <div class="relative z-[1200]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white p-6">
              <button (click)="closeRejectionModal()" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors">
                <span class="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
              <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg class="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg font-semibold leading-6 text-slate-900" id="modal-title">Confirm Application Rejection</h3>
                <div class="mt-2"><p class="text-sm text-slate-500">Provide a clear reason for rejecting this application. This information is critical for our records and may be shared with the applicant.</p></div>
              </div>
              <div class="mt-5 sm:mt-6">
                <label for="rejection-reason" class="block text-sm font-medium leading-6 text-slate-900">Reason for Rejection</label>
                <div class="mt-2">
                  <textarea [(ngModel)]="rejectionReason" id="rejection-reason" name="rejection-reason" rows="4" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="Enter reason here..."></textarea>
                </div>
                <div class="mt-3 rounded-md bg-yellow-50 p-3">
                  <div class="flex items-start">
                    <div class="flex-shrink-0"><svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" /></svg></div>
                    <div class="ml-3"><p class="text-sm text-yellow-700">A clear reason is required for rejection. Ensure your notes are professional and constructive.</p></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-slate-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl">
              <button (click)="submitRejection()" [disabled]="!rejectionReason() || actionInProgress()" type="button" class="inline-flex w-full justify-center items-center rounded-md bg-red-600 hover:bg-red-700 focus-visible:outline-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors">
                @if(actionInProgress()) {
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span>Submitting...</span>
                } @else {
                  <span>Submit Rejection</span>
                }
              </button>
              <button (click)="closeRejectionModal()" type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Approval Modal -->
  @if(showApprovalModal()) {
    <div class="relative z-[1200]" aria-labelledby="approval-modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white p-6">
              <button (click)="closeApprovalModal()" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors">
                <span class="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
              <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                 <svg class="h-7 w-7 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg font-semibold leading-6 text-slate-900" id="approval-modal-title">Finalize Approval & Create System Account</h3>
                <div class="mt-2"><p class="text-sm text-slate-500">Review and confirm the system details below. These will be used to create the new account.</p></div>
              </div>

              <div class="mt-5 sm:mt-6 space-y-4">
                <div>
                  <label for="logonDomain" class="block text-sm font-medium leading-6 text-slate-900">Logon Domain</label>
                  <div class="mt-2"><input type="text" id="logonDomain" [(ngModel)]="logonDomain" (input)="validateLogonDomain()" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"></div>
                  @if (logonDomainError(); as error) { <p class="mt-2 text-sm text-red-600">{{ error }}</p> }
                </div>
                <div>
                  <label for="accountShortName" class="block text-sm font-medium leading-6 text-slate-900">Account Short Name</label>
                  <div class="mt-2"><input type="text" id="accountShortName" [(ngModel)]="accountShortName" (input)="validateAccountShortName()" maxlength="16" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"></div>
                  @if (accountShortNameError(); as error) { <p class="mt-2 text-sm text-red-600">{{ error }}</p> }
                </div>
                <div>
                  <label for="approvalNotes" class="block text-sm font-medium leading-6 text-slate-900">Approval Notes</label>
                  <div class="mt-2"><textarea [(ngModel)]="approvalNotes" id="approvalNotes" rows="3" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="Enter notes here..."></textarea></div>
                </div>
                 @if (approvalError(); as error) {
                    <div class="rounded-md bg-red-50 p-3"><div class="flex"><div class="flex-shrink-0"><svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.293 7.293a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 7.293 8.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg></div><div class="ml-3"><p class="text-sm text-red-700">{{ error }}</p></div></div></div>
                 }
              </div>
            </div>
            <div class="bg-slate-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl">
              <button (click)="submitApproval()" [disabled]="!approvalNotes() || logonDomainError() || accountShortNameError() || actionInProgress()" type="button" class="inline-flex w-full justify-center items-center rounded-md bg-green-600 hover:bg-green-700 focus-visible:outline-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors">
                 @if(actionInProgress()) {
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span>Approving...</span>
                } @else {
                  <span>Confirm Approval</span>
                }
              </button>
              <button (click)="closeApprovalModal()" type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Rollback Modal -->
  @if (showRollbackModal()) {
    <div class="relative z-[1200]" aria-labelledby="rollback-modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white p-6">
              <button (click)="closeRollbackModal()" [disabled]="actionInProgress()" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50">
                <span class="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
              <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg class="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg font-semibold leading-6 text-slate-900" id="rollback-modal-title">Reject and Remove Customer</h3>
                <div class="mt-2">
                  <p class="text-sm text-slate-500">This will permanently remove all traces of this customer from Pooling Hub, including their database, provisioned services, and associated documents.</p>
                  <p class="mt-3 text-sm font-semibold text-red-600">This action cannot be undone.</p>
                  @if (rollbackError(); as err) {
                    <p class="mt-3 text-sm text-red-600">{{ err }}</p>
                  }
                </div>
              </div>
            </div>
            <div class="bg-slate-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl">
              <button
                (click)="confirmRollback()"
                [disabled]="actionInProgress()"
                type="button"
                class="inline-flex w-full justify-center items-center rounded-md bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                @if (actionInProgress()) {
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span>Removing...</span>
                } @else {
                  <span>Reject and Remove</span>
                }
              </button>
              <button (click)="closeRollbackModal()" [disabled]="actionInProgress()" type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto disabled:opacity-50 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Suspend Modal -->
  @if (showSuspendModal()) {
    <div class="relative z-[1200]" aria-labelledby="suspend-modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white p-6">
              <button (click)="closeSuspendModal()" [disabled]="actionInProgress()" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50">
                <span class="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
              <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
                <svg class="h-7 w-7 text-orange-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg font-semibold leading-6 text-slate-900" id="suspend-modal-title">Suspend System</h3>
                <div class="mt-2">
                  <p class="text-sm text-slate-500">This will tear down the fleet's portal and SuperYes containers and mark the system as inactive in Pooling Hub.</p>
                  <p class="mt-3 text-sm font-semibold text-slate-700">The database and fleet data will remain intact so the system can be reinstated later.</p>
                </div>
              </div>
            </div>
            <div class="bg-slate-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl">
              <button
                (click)="confirmSuspend()"
                [disabled]="actionInProgress()"
                type="button"
                class="inline-flex w-full justify-center items-center rounded-md bg-orange-600 hover:bg-orange-700 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                @if (actionInProgress()) {
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span>Suspending...</span>
                } @else {
                  <span>Suspend System</span>
                }
              </button>
              <button (click)="closeSuspendModal()" [disabled]="actionInProgress()" type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto disabled:opacity-50 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Reinstate Modal -->
  @if (showReinstateModal()) {
    <div class="relative z-[1200]" aria-labelledby="reinstate-modal-title" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white p-6">
              <button (click)="closeReinstateModal()" [disabled]="actionInProgress()" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50">
                <span class="sr-only">Close</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
              <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-lime-100">
                <svg class="h-7 w-7 text-lime-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9.348 14.652a3.75 3.75 0 0 1 0-5.304m5.304 0a3.75 3.75 0 0 1 0 5.304m-7.425 2.121a6.75 6.75 0 0 1 0-9.546m9.546 0a6.75 6.75 0 0 1 0 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12Z" /></svg>
              </div>
              <div class="mt-3 text-center sm:mt-5">
                <h3 class="text-lg font-semibold leading-6 text-slate-900" id="reinstate-modal-title">Reinstate System</h3>
                <div class="mt-2">
                  <p class="text-sm text-slate-500">This will bring the fleet's database schema up to date, re-instantiate the portal and SuperYes containers, and mark the system as active in Pooling Hub.</p>
                  <p class="mt-3 text-sm font-semibold text-slate-700">The system will move back to Live once reinstated.</p>
                </div>
              </div>
            </div>
            <div class="bg-slate-50 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl">
              <button
                (click)="confirmReinstate()"
                [disabled]="actionInProgress()"
                type="button"
                class="inline-flex w-full justify-center items-center rounded-md bg-lime-600 hover:bg-lime-700 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                @if (actionInProgress()) {
                  <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span>Reinstating...</span>
                } @else {
                  <span>Reinstate System</span>
                }
              </button>
              <button (click)="closeReinstateModal()" [disabled]="actionInProgress()" type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-100 sm:mt-0 sm:w-auto disabled:opacity-50 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Document Viewer -->
  @if (viewingDocument(); as doc) {
    <div class="relative z-[1100]" role="dialog" aria-modal="true">
      <div class="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity"></div>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-2 sm:p-4">
          <div class="relative flex w-full max-w-screen-2xl h-[96vh] text-left text-base transition-all">
            <div class="relative flex w-full h-full flex-col overflow-hidden bg-white p-6 rounded-2xl shadow-2xl">
              <div class="flex-shrink-0 flex items-center justify-between pb-4 border-b">
                <h2 class="text-lg font-medium text-slate-900">Document Review: <span class="font-bold">{{ formatDocumentType(doc.document_type) }}</span></h2>
                <button (click)="closeDocumentViewer()" type="button" class="text-slate-400 hover:text-slate-500">
                  <span class="sr-only">Close</span>
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div class="flex-grow grid w-full grid-cols-1 items-start gap-x-8 sm:grid-cols-12 mt-6 min-h-0">
                <div class="flex justify-center items-center overflow-hidden rounded-lg bg-slate-100 sm:col-span-8 lg:col-span-9 h-full border">
                   @if (documentUrlLoading()) {
                    <div class="flex items-center justify-center h-full"><p class="text-slate-500">Loading document...</p></div>
                   } @else if (documentUrl(); as url) {
                      @if (isPdf(doc.storage_path)) {
                        <iframe [src]="sanitizer.bypassSecurityTrustResourceUrl(url)" class="w-full h-full"></iframe>
                      } @else {
                        <canvas #docCanvas
                          [style.cursor]="canvasCursor()"
                          (mousedown)="onCanvasMouseDown($event)"
                          (mousemove)="onCanvasMouseMove($event)"
                          (mouseup)="onCanvasMouseUp($event)"
                          (mouseleave)="onCanvasMouseLeave($event)"
                          (touchstart)="onCanvasTouchStart($event)"
                          (touchmove)="onCanvasTouchMove($event)"
                          (touchend)="onCanvasTouchEnd($event)"
                          (touchcancel)="onCanvasTouchEnd($event)">
                        </canvas>
                      }
                   } @else {
                    <div class="flex items-center justify-center h-full"><p class="text-red-500">Could not load document.</p></div>
                   }
                </div>

                <div class="sm:col-span-4 lg:col-span-3">
                  <h3 class="text-base font-semibold text-slate-800">Document Actions</h3>
                  @if (viewingEnhancedDocument()?.status === 'approved') {
                    <div class="mt-4 rounded-lg bg-green-50 p-4 text-sm text-green-700">This document has been approved.</div>
                  } @else {
                    <div class="mt-4 space-y-4">
                      @if (!isPdf(doc.storage_path) && cvReady()) {
                        <div>
                          @if (isCropped()) {
                            <h4 class="text-sm font-medium text-slate-600">Cropped Image</h4>
                            <div class="mt-2 space-y-2">
                              <button (click)="saveCroppedDocument()" [disabled]="isSaving()" class="flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-slate-300 disabled:cursor-not-allowed">
                                {{ isSaving() ? 'Saving...' : 'Save Cropped Image' }}
                              </button>
                              <button (click)="undoCrop()" [disabled]="isSaving()" class="flex w-full items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50">
                                Undo Crop
                              </button>
                              @if (saveError(); as err) {
                                <p class="text-sm text-red-600">{{ err }}</p>
                              }
                            </div>
                          } @else {
                            <h4 class="text-sm font-medium text-slate-600">Image Tools</h4>
                            <div class="mt-2 grid grid-cols-2 gap-2">
                               <button (click)="detectDocumentEdges()" [disabled]="!originalImage() || manualCornerMode()" class="inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50">Detect Edges</button>
                               <button (click)="startManualCornerSelection()" [disabled]="!originalImage() || manualCornerMode()" class="inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50">Select Corners</button>
                               <button (click)="applyCrop()" [disabled]="!detectedCorners()" class="inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50">Apply Crop</button>
                               <button (click)="resetImage()" [disabled]="!detectedCorners() && !manualCornerMode()" class="inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50">Reset</button>
                            </div>
                            @if (manualCornerMode()) {
                              <p class="mt-2 text-xs text-center text-slate-500">Click the 4 corners of the document ({{ manualCornerPoints().length }}/4)</p>
                            }
                          }
                        </div>
                        <hr/>
                      }
                      <div>
                        <label for="expiry_date" class="block text-sm font-medium leading-6 text-slate-900">Expiry Date</label>
                        <div class="mt-2">
                          <input type="date" [(ngModel)]="documentExpiryDate" id="expiry_date" name="expiry_date" class="block w-full rounded-md border-0 py-1.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                        </div>
                      </div>
                      <button (click)="approveDocument()" [disabled]="!isExpiryDateValid() || isApprovingDocument()" type="button" class="flex w-full items-center justify-center rounded-md border border-transparent bg-green-600 px-8 py-3 text-base font-medium text-white hover:bg-green-700 disabled:bg-slate-300">
                        @if (isApprovingDocument()) { <span>Approving...</span> } @else { <span>Approve Document</span> }
                      </button>

                      @if(documentApprovalError(); as error) {
                        <div class="rounded-md bg-red-50 p-3">
                          <div class="flex">
                            <div class="flex-shrink-0">
                              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.293 7.293a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 7.293 8.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                            </div>
                            <div class="ml-3">
                              <p class="text-sm text-red-700">{{ error }}</p>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>
